// DynamicMedia.tsx
import React, { useEffect, useRef, useState } from 'react';
import Lottie, { LottieRefCurrentProps } from "lottie-react";

interface DynamicMediaProps {
  src: string;
  alt?: string;
  ariaLabel?:string;
  className?: string;
  style?: React.CSSProperties;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement>;
  imgProps?: React.ImgHTMLAttributes<HTMLImageElement>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  renderContainer?: boolean;
  lottieClassName?:string
  videoAutoplay?:boolean
  lottieProps?: {
    loop?: boolean;
    autoplay?: boolean;
    rendererSettings?: any;
    animationData?: any;
    style?: React.CSSProperties;
    lottieRef?: React.RefObject<LottieRefCurrentProps>;
    onComplete?: () => void;
    [key: string]: any; // To allow any additional Lottie props
  };
}

const DynamicMedia: React.FC<DynamicMediaProps> = ({
  src,
  alt = '',
  ariaLabel='',
  className = '',
  style = {},
  videoProps = {},
  imgProps = {},
  containerProps = {},
  renderContainer = false,
  lottieProps = {},
  lottieClassName='',
  videoAutoplay=true
}) => {

  const lottieRef = useRef<LottieRefCurrentProps>(null);
  
  const isLottie = (url: string): boolean => {
    return url.endsWith('.json');
  };

  const isVideo = (url: string): boolean => {
    const videoFormats = ["webm", "mp4", "mov", "avi", "wmv", "flv", "mkv"];
    const extension = url.split('.').pop()?.toLowerCase();
    return extension ? videoFormats.includes(extension) : false;
  };
    
    // If we need to fetch the animation data from the URL
    const [animationData, setAnimationData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      const fetchLottieData = async () => {
        try {
          setIsLoading(true);
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error(`Failed to fetch Lottie animation: ${response.status}`);
          }
          const data = await response.json();
          setAnimationData(data);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          console.error('Error fetching Lottie animation:', err);
        } finally {
          setIsLoading(false);
        }
      };

      fetchLottieData();
    }, [src]);
  // For Lottie animations, we need to fetch the JSON data
  const renderLottie = () => {
    const defaultStyle = { width: "100%", height: "100%" };
    const mergedStyle = { ...defaultStyle, ...style, ...(lottieProps.style || {}) };
    
    // If animationData is directly provided
    if (lottieProps.animationData) {
      return (
        <Lottie
          lottieRef={lottieProps.lottieRef || lottieRef}
          animationData={lottieProps.animationData}
          loop={lottieProps.loop !== undefined ? lottieProps.loop : false}
          className={lottieClassName}
          style={mergedStyle}
          aria-label={ariaLabel}
          {...lottieProps}
        />
      );
    }


    if (isLoading) {
      return <div className={`${className} lottie-loading`} style={mergedStyle}>Loading animation...</div>;
    }

    if (error) {
      return <div className={`${className} lottie-error`} style={mergedStyle}>Failed to load animation</div>;
    }

    return (
      <Lottie
        lottieRef={lottieProps.lottieRef || lottieRef}
        animationData={animationData}
        loop={lottieProps.loop !== undefined ? lottieProps.loop : false}
        className={lottieClassName}
        style={mergedStyle}
        aria-label={ariaLabel}
        {...lottieProps}
      />
    );
  };

  let mediaElement;
  
  if (isLottie(src)) {
    mediaElement = renderLottie();
  } else if (isVideo(src)) {
    mediaElement = (
      <video
        src={src}
        autoPlay={videoAutoplay}
        loop
        muted
        playsInline
        className={className}
        style={style}
        {...videoProps}
        aria-label={ariaLabel}

      />
    );
  } else {
    mediaElement = (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        {...imgProps}
      />
    );
  }

  if (renderContainer) {
    return (
      <div {...containerProps}>
        {mediaElement}
      </div>
    );
  }

  return mediaElement;
};

export default DynamicMedia;